import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { UserService } from "src/core/services/user.service";
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Attribute, User, UserListQuery } from 'src/interfaces/user';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

type UserListEnabledUi = 'enabled_only' | 'all' | 'disabled';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
    standalone: false
})
export class UserListComponent implements OnInit {

  displayedColumns: string[] = [
    'name',
    'email',
    'phone_number',
    'company_or_individual',
    'status',
    'enabled',
    'action'
  ];

  usersDataSource = new MatTableDataSource<User>([]);
  page: number = 1;
  limit: number = 10;
  totalNumberOfUsers: number = 0;
  perPageControl = this._formBuilder.control('10');
  searchControl = new UntypedFormControl('');
  /** Company / vet (individual) name — sent as search_practice */
  practiceSearchControl = new UntypedFormControl('');
  /** Default: enabled users only (omit enabled param). */
  enabledFilter = new UntypedFormControl('enabled_only' as UserListEnabledUi);
  lastToken: string | null = null;
  nextToken: string | null = null;
  tokens: any;
  loading: boolean = false;
  /** Monotonically increasing id to ignore stale responses. */
  private currentRequestId = 0;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private _userService: UserService,
    private _localStorageService: LocalStorageService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
  ) {
    const limit = this._localStorageService.get('USERS', 'LIMIT');

    if (limit) {
      this.limit = parseInt(limit);
    }

    this.tokens = [];
    this.nextToken = this._userService.getPaginationToken();
    this.perPageControl.valueChanges.subscribe((value) => {
      this.limit = parseInt(value);
      this.reloadFromStart();
    });
    this.perPageControl.setValue(String(this.limit), { emitEvent: false });
  }

  ngOnInit(): void {
    this.applyResolvedUsers(this._route.snapshot.data['users']);

    this.applyQueryParamsToForm(this._route.snapshot.queryParamMap);

    this.searchControl.valueChanges.subscribe(() => this.reloadFromStart());
    this.practiceSearchControl.valueChanges.subscribe(() => this.reloadFromStart());
    this.enabledFilter.valueChanges.subscribe(() => this.reloadFromStart());

    this._route.data.subscribe((data) => {
      this.applyResolvedUsers(data['users']);
    });
  }

  private applyResolvedUsers(users: User[] | null | undefined): void {
    this.usersDataSource.data = users ?? [];
    this.totalNumberOfUsers = this._userService.getTotalNumberOfUsers();
    this.usersDataSource.sort = this.sort;

    this.usersDataSource.filterPredicate = (user: User, filter: string): boolean => {
      const name = this.getAttribute('name', user).toLowerCase();
      return name.includes(filter);
    };
  }

  /** Build API query from current filters. */
  private buildListQuery(): UserListQuery {
    const en = this.enabledFilter.value;
    return {
      search: (this.searchControl.value || '').trim(),
      searchPractice: (this.practiceSearchControl.value || '').trim(),
      enabled: en === 'all' ? 'all' : en === 'disabled' ? 'disabled' : 'default',
    };
  }

  /** Sync form from URL (resolver already fetched with same params). */
  private applyQueryParamsToForm(map: ParamMap): void {
    this.searchControl.setValue(map.get('search') ?? '', { emitEvent: false });
    this.practiceSearchControl.setValue(
      map.get('search_practice') || map.get('practice') || '',
      { emitEvent: false }
    );
    const en = map.get('enabled');
    let ui: UserListEnabledUi = 'enabled_only';
    if (en) {
      const v = en.toLowerCase();
      if (v === 'all' || v === 'both' || v === '*') ui = 'all';
      else if (v === '0' || v === 'false' || v === 'disabled') ui = 'disabled';
    }
    this.enabledFilter.setValue(ui, { emitEvent: false });
  }

  /** Query params when opening user detail or sharing state. */
  buildListQueryParams(): Record<string, string> {
    const q: Record<string, string> = {};
    const s = (this.searchControl.value || '').trim();
    if (s) q.search = s;
    const sp = (this.practiceSearchControl.value || '').trim();
    if (sp) q.search_practice = sp;
    const ef = this.enabledFilter.value;
    if (ef === 'all') q.enabled = 'all';
    else if (ef === 'disabled') q.enabled = '0';
    return q;
  }

  reloadFromStart(): void {
    this.page = 1;
    this.tokens = [];
    this.lastToken = null;
    this.nextToken = null;
    this.getUsers(null);
  }

  async lastPage() {
    try {
      let token = this.tokens.pop();
      await this.getUsers(token);
      this.page--;
    } catch (err) {
      console.log(err);
    }
  }
  async nextPage() {
    try {
      if (this.lastToken) {
        this.tokens.push(this.lastToken);
      }
      await this.getUsers(this.nextToken);
      this.page++;
    } catch (err) {
      console.log(err);
    }
  }

  getUsers(token: string | null = null) {
    const requestId = ++this.currentRequestId;
    this.loading = true;
    const listQuery = this.buildListQuery();
    return new Promise(async (resolve, reject) => {
      try {
        const newUsers = await this._userService.getUsers(this.limit, token, listQuery);
        if (requestId !== this.currentRequestId) {
          this.loading = false;
          resolve(false);
          return;
        }
        this.usersDataSource.data = [...newUsers];
        this.totalNumberOfUsers = this._userService.getTotalNumberOfUsers();
        this.lastToken = this.nextToken;
        this.nextToken = this._userService.getPaginationToken();
        this.loading = false;
        resolve(true);
      } catch (err) {
        if (requestId === this.currentRequestId) {
          this.loading = false;
        }
        reject(err);
      }
    });
  }

  viewUser(user: User) {
    this._router.navigate(['/portal/users/view', user.Username], {
      queryParams: this.buildListQueryParams(),
    });
  }

  async disableUser(event: Event, user: User) {
    event.stopPropagation();
    const success = await this._userService.disableUser(user.Username);
    if (success) await this.refresh();
  }

  async enableUser(event: Event, user: User) {
    event.stopPropagation();
    const success = await this._userService.enableUser(user.Username);
    if (success) await this.refresh();
  }

  async refresh() {
    const users = await this._userService.getUsers(this.limit, null, this.buildListQuery());
    this.usersDataSource.data = users;
    this.totalNumberOfUsers = this._userService.getTotalNumberOfUsers();
    this.usersDataSource.sort = this.sort;
  }

  getAttribute(attributeName: string, user: User) {
    const attribute: Attribute | undefined
      = user.Attributes?.find(attr => attr.Name === attributeName);

    if (!attribute) return "";

    return attribute.Value;
  }

  /**
   * Display label for company / individual column based on list API fields.
   * Prefers company_name when present, otherwise falls back to individual name.
   */
  getCompanyOrIndividualLabel(user: User): string {
    if (user.company_name) {
      return user.company_name;
    }
    const first = user.individual_first_name ?? '';
    const last = user.individual_last_name ?? '';
    const full = `${first} ${last}`.trim();
    return full || '';
  }

  /** Backend may send boolean or 0/1; treat as enabled for UI and actions. */
  isUserEnabled(user: User): boolean {
    const v = user.Enabled as boolean | number | string | undefined | null;
    if (v === true || v === 1 || v === '1') return true;
    if (v === false || v === 0 || v === '0') return false;
    return !!v;
  }

  /** Safe status label; list API may return null UserStatus (e.g. partial Cognito records). */
  normalizeText(text: string | null | undefined): string {
    if (text == null || String(text).trim() === '') return '';
    const s = String(text);
    return s.charAt(0) + s.slice(1).replaceAll('_', ' ').toLowerCase();
  }
}
